// The fetch client (docs/CONTRACTS.md sections 5 to 7). Same-origin JSON over fetch; the browser adds the session
// cookie, Origin and Sec-Fetch-Site (the CSRF rule). URLs carry ids and codes only: apiPath() refuses anything else,
// so search text, names and prompts can only travel in a POST body.
import type { ErrorCode } from '@nw/shared/errors'
import { noteBuild } from './build.ts'

/** A failed API call. `status` 0 with code 'offline' means the request never reached the server; 'unavailable' is a 5xx with no error body (a gateway answered: the server is not). */
export class ApiError extends Error {
  readonly status: number
  readonly code: ErrorCode | 'offline' | 'unavailable' | 'bad_response'
  readonly issues?: { path: string; code: string }[]
  readonly current?: unknown

  constructor(status: number, code: ApiError['code'], message: string, extra?: { issues?: ApiError['issues']; current?: unknown }) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.issues = extra?.issues
    this.current = extra?.current
  }
}

/** An id, a code, an enum value or a number: the only things a URL may carry (`urlSafe` on the server). */
const URL_SAFE = /^[A-Za-z0-9_.~-]{1,128}$/

function safe(name: string, value: string | number): string {
  const text = String(value)
  if (!URL_SAFE.test(text) || /^\.+$/.test(text)) throw new Error(`apiPath: ${name} is not an id or a code`)
  return encodeURIComponent(text)
}

/**
 * Builds an API URL from a route path and its parameters: apiPath('/api/orgs/:orgnr', { orgnr }),
 * apiPath('/api/my/contacts', {}, { after, limit: 50 }). Throws on free text in a parameter or a query value.
 */
export function apiPath(
  template: `/api/${string}`,
  params: Record<string, string | number> = {},
  query: Record<string, string | number | undefined | null> = {},
): string {
  const path = template.replace(/:([A-Za-z]+)/g, (_whole, name: string) => {
    const value = params[name]
    if (value === undefined) throw new Error(`apiPath: missing parameter ${name}`)
    return safe(name, value)
  })
  const pairs = Object.entries(query).filter((e): e is [string, string | number] => e[1] !== undefined && e[1] !== null)
  if (pairs.length === 0) return path
  return `${path}?${pairs.map(([k, v]) => `${safe('query key', k)}=${safe(k, v)}`).join('&')}`
}

type Listener = () => void
const unauthenticated = new Set<Listener>()

/** Called whenever any API answer is 401 (stale cookie, logout elsewhere, revoked session). */
export function onUnauthenticated(listener: Listener): () => void {
  unauthenticated.add(listener)
  return () => unauthenticated.delete(listener)
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  headers?: Record<string, string>
}

/** Sends a request and returns the raw Response once it is OK; throws ApiError otherwise. */
export async function apiResponse(path: string, { method = 'GET', body, signal, headers }: RequestOptions = {}): Promise<Response> {
  if (!path.startsWith('/api/')) throw new Error('apiResponse: only /api/ paths')
  let res: Response
  try {
    res = await fetch(path, {
      method,
      signal,
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { accept: 'application/json', ...(body === undefined ? {} : { 'content-type': 'application/json' }), ...headers },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch (e) {
    if (signal?.aborted) throw e
    throw new ApiError(0, 'offline', 'offline')
  }
  noteBuild(res) // plan section E: an answer from a newer release makes the app look for its new version
  if (res.ok) return res
  if (res.status === 401) unauthenticated.forEach((l) => l())
  let parsed: unknown = null
  try {
    parsed = await res.json()
  } catch {
    /* not JSON: a proxy or gateway answered */
  }
  const err = (parsed as { error?: { code?: string; message?: string; issues?: ApiError['issues']; current?: unknown } } | null)?.error
  if (err?.code) throw new ApiError(res.status, err.code as ErrorCode, err.message ?? err.code, { issues: err.issues, current: err.current })
  throw new ApiError(res.status, res.status >= 500 ? 'unavailable' : 'bad_response', `HTTP ${res.status}`)
}

/** JSON request: apiFetch<Org>(apiPath('/api/orgs/:orgnr', { orgnr })). */
export async function apiFetch<T>(path: string, options?: RequestOptions): Promise<T> {
  const res = await apiResponse(path, options)
  if (res.status === 204) return undefined as T
  try {
    return (await res.json()) as T
  } catch {
    noteBuild(res, false) // a release this client cannot read: the app reloads into its new version
    throw new ApiError(res.status, 'bad_response', 'The answer was not JSON')
  }
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => apiFetch<T>(path, { signal }),
  post: <T>(path: string, body: unknown = {}, signal?: AbortSignal) => apiFetch<T>(path, { method: 'POST', body, signal }),
  patch: <T>(path: string, body: unknown, signal?: AbortSignal) => apiFetch<T>(path, { method: 'PATCH', body, signal }),
  delete: <T>(path: string, signal?: AbortSignal) => apiFetch<T>(path, { method: 'DELETE', signal }),
}
